import {
  RelatorioComposicaoReceitasHorizontalItem,
  RelatorioComposicaoReceitasHorizontalPdfResponse,
  ResumoCategoriaItem,
} from "core/features/relatorios/typings";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import xlsx, { IColumn, IContent, IJsonSheet, ISettings } from "json-as-xlsx";
import moment from "moment";
import * as _ from "lodash";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

function getContent(
  data: RelatorioComposicaoReceitasHorizontalPdfResponse,
  groupByFields?: string[],
) {
  if (groupByFields?.length) {
    const groups = _.groupBy(data.items, (item) => {
      let key = "";

      groupByFields.forEach((field) => {
        if (field in item) {
          key += item[field as keyof RelatorioComposicaoReceitasHorizontalItem];
        }
      });

      return key;
    });

    const sortedGroupKeys = Object.keys(groups).sort((a, b) =>
      a.localeCompare(b),
    );

    const _items: (Omit<RelatorioComposicaoReceitasHorizontalItem, "valor"> & {
      valor?: number | string;
      totalCreditado?: number;
    })[] = [];

    for (const group of sortedGroupKeys) {
      const groupItems = groups[group];

      const groupTotal = groupItems.reduce((acc, next) => {
        const categoriasASomar = Object.entries(next.composicoes);

        const soma = categoriasASomar.reduce(
          (acc, [_, valor]) => (acc += valor),
          0,
        );

        return (acc += soma);
      }, 0);

      _items.push(...groupItems);
      _items.push({
        composicoes: {},
        dataCompetencia: "",
        dataMovimentacao: "",
        dataPagamento: "",
        dataVencimento: "",
        descricao: "",
        id: group,
        pessoa: "",
        unidade: "",
        blocoQuadra: "",
        unidadeId: "",
        valor: "",
        totalCreditado: groupTotal,
        nossoNumero: "",
      });
    }

    return _items.map(defaultContentMapperXlsx);
  }

  return data.items.map(defaultContentMapperXlsx);
}

export function exportExcelRelatorioComposicaoReceitasHorizontal({
  data,
  todasAsCategorias = [],
  groupByFields,
}: {
  data: RelatorioComposicaoReceitasHorizontalPdfResponse;
  todasAsCategorias: { id: string; nome: string; breadcrumb: string }[];
  groupByFields?: string[];
}) {
  const settings: ISettings = {
    fileName: `Relatório de Receitas com Composição Horizontal - ${moment().format(
      "DD_MM_YYYY",
    )}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columns = getColumns({ todasAsCategorias, groupByFields });

  const sheets: IJsonSheet[] = [];

  const content = getContent(data, groupByFields);

  sheets.push({
    sheet: "Receitas",
    columns: columns,
    content,
  });

  const columnsResumo = getColumnsResumo();

  sheets.push({
    sheet: "Resumo por categoria",
    columns: columnsResumo,
    content: data.resumoPorCategoria.map(defaultContentMapperXlsx),
  });

  xlsx(sheets, settings);
}

function getColumns({
  todasAsCategorias = [],
  groupByFields,
}: {
  todasAsCategorias: { id: string; nome: string; breadcrumb: string }[];
  groupByFields?: string[];
}) {
  const columns: IColumn[] = [];

  columns.push({
    label: "Unidade",
    value: (row: IContent) =>
      formatBlocoQuadra(
        (row as unknown as RelatorioComposicaoReceitasHorizontalItem)?.unidade,
        (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
          ?.blocoQuadra,
        "",
      ),
  });

  columns.push({
    label: "Nome",
    value: (row: IContent) =>
      (row as unknown as RelatorioComposicaoReceitasHorizontalItem)?.pessoa,
  });

  columns.push({
    label: "Competência",
    format: "mm/yyyy",
    value: (row: IContent) => {
      const _dataCompetencia = (
        row as unknown as RelatorioComposicaoReceitasHorizontalItem
      ).dataCompetencia;

      return _dataCompetencia && moment(_dataCompetencia, "YYYY-MM").isValid()
        ? moment(_dataCompetencia, "YYYY-MM").format("MM/YYYY")
        : "";
    },
  });

  columns.push({
    label: "Vencimento",
    format: "dd/mm/yyyy",
    value: (row: IContent) => {
      const date = (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
        .dataVencimento;

      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
  });

  columns.push({
    label: "Data de Liquidação",
    format: "dd/mm/yyyy",
    value: (row: IContent) => {
      const date = (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
        .dataPagamento;

      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
  });

  columns.push({
    label: "Data de Crédito",
    format: "dd/mm/yyyy",
    value: (row: IContent) => {
      const date = (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
        .dataMovimentacao;

      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
  });

  columns.push({
    label: "Nosso Número",
    value: (row: IContent) =>
      (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
        .nossoNumero ?? "",
  });

  if (groupByFields?.length) {
    columns.push({
      label: "Sub-Total",
      format: "#,###0.00",
      value: (row: IContent) => {
        const categoriasASomar = Object.entries(
          (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
            .composicoes,
        );

        const soma = categoriasASomar.reduce(
          (acc, [_, valor]) => (acc += valor),
          0,
        );

        return soma > 0 ? soma : "";
      },
    });

    columns.push({
      label: "Total Creditado",
      format: "#,###0.00",
      value: (row: IContent) => {
        return (
          (row as unknown as { totalCreditado: number }).totalCreditado ?? ""
        );
      },
    });
  } else {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) => {
        const categoriasASomar = Object.entries(
          (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
            .composicoes,
        );

        const soma = categoriasASomar.reduce(
          (acc, [_, valor]) => (acc += valor),
          0,
        );

        return soma ?? 0;
      },
    });
  }

  if (todasAsCategorias?.length) {
    todasAsCategorias.forEach((categoria) =>
      columns.push({
        label: categoria.nome,
        format: "#,###0.00",
        value: (row: IContent) =>
          (row as unknown as RelatorioComposicaoReceitasHorizontalItem)
            .composicoes[categoria.id] ?? 0,
      }),
    );
  }

  return columns;
}

function getColumnsResumo() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Categoria",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).nome,
  });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).valor,
  });

  return columns;
}
